import NumberInput from '../../../components/NumberInput'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components/ui/button'
import { Plus } from 'lucide-react'
import { useUnits } from '../../../contexts/UnitsContext'

const FIELDS = ['neck', 'chest', 'belly', 'waist', 'arm', 'thigh']

export function MeasurementForm({ onSubmit, loading }) {
  const { t } = useTranslation()
  const { lengthUnit, lengthToCm } = useUnits()
  const today = new Date().toLocaleDateString('en-CA')
  const [date, setDate] = useState(today)
  const [values, setValues] = useState({})
  const [bodyFat, setBodyFat] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (field, val) => {
    setValues((prev) => ({ ...prev, [field]: val }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const hasAnyMeasurement = FIELDS.some((f) => values[f] && Number(values[f]) > 0)
    const hasBodyFat = bodyFat && Number(bodyFat) > 0
    if (!date || (!hasAnyMeasurement && !hasBodyFat)) return

    setSubmitting(true)
    const data = { date, note: note.trim() || null, body_fat: hasBodyFat ? Number(bodyFat) : null }
    FIELDS.forEach((f) => {
      data[f] = values[f] ? lengthToCm(values[f]) : null
    })
    const success = await onSubmit(data)
    if (success) {
      setValues({})
      setBodyFat('')
      setNote('')
    }
    setSubmitting(false)
  }

  const hasAnyMeasurement = FIELDS.some((f) => values[f] && Number(values[f]) > 0)
  const hasBodyFat = bodyFat && Number(bodyFat) > 0
  const isDisabled = loading || submitting || (!hasAnyMeasurement && !hasBodyFat)

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        dir="ltr"
        className="h-11 px-3 rounded-lg border border-input bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
        required
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {FIELDS.map((field) => (
          <div key={field}>
            <label className="block text-xs text-muted-foreground mb-1">
              {t(`phoenixTracker.measurements.${field}`)} ({lengthUnit})
            </label>
            <NumberInput
              inputMode="decimal"
              step="any"
              min="0"
              value={values[field] || ''}
              onChange={(e) => handleChange(field, e.target.value)}
              placeholder="0.0"
              className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
            />
          </div>
        ))}
      </div>
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('phoenixTracker.measurements.bodyFat')} (%)
        </label>
        <NumberInput
          inputMode="decimal"
          step="any"
          min="0"
          max="100"
          value={bodyFat}
          onChange={(e) => setBodyFat(e.target.value)}
          placeholder="0.0"
          className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
        />
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t('phoenixTracker.measurements.notePlaceholder')}
        rows={2}
        className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500 resize-y"
      />
      <Button
        type="submit"
        disabled={isDisabled}
        className="w-full h-11 bg-app-primary-500 hover:bg-app-primary-600 text-white"
      >
        <Plus className="w-4 h-4 mr-1" />
        {submitting ? t('phoenixTracker.saving') : t('phoenixTracker.measurements.add')}
      </Button>
    </form>
  )
}
